import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { scrollToPricing } from '../lib/stripe';
import { scrollToElement, scrollWindowToTop } from '../lib/scroll';
import { LOGO_URL } from '../assets/index';

const languages = [
  { code: 'pt-br', label: 'Português', flag: '🇧🇷' },
  { code: 'en-us', label: 'English', flag: '🇺🇸' },
  { code: 'es-la', label: 'Español', flag: '🇪🇸' },
];

const Navbar = () => {
  const { language, toggleLanguage } = useLanguage();
  const text = t[language].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on language change
  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [language]);

  const scrollTo = (id) => {
    scrollToElement(id);
    setMobileOpen(false);
  };

  const handleCta = () => {
    setMobileOpen(false);
    scrollToPricing();
  };

  const handleLangSelect = (code) => {
    toggleLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const navLinks = [
    { id: 'features', label: text.features },
    { id: 'pricing', label: text.pricing },
    { id: 'testimonials', label: text.testimonials },
    { id: 'faq', label: text.faq },
  ];

  const currentLang = languages.find((l) => l.code === language);

  return (
    <>
      <motion.nav
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`sticky top-0 z-50 transition-all duration-500 bg-[#0a0a0f] border-b border-white/5 shadow-2xl shadow-black/50 ${scrolled ? 'py-3' : 'py-4'}`}
      >
        <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Logo */}
          <motion.div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => scrollWindowToTop()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img src={LOGO_URL} alt="LovaGO Logo" className="w-9 h-9 object-contain" />
            <span className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 tracking-tight">
              LovaGO
            </span>
          </motion.div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6 text-gray-400 font-medium text-sm">
            {navLinks.map((link, i) => (
              <motion.button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="hover:text-white transition-colors relative group"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.3 }}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-500 to-purple-600 group-hover:w-full transition-all duration-300" />
              </motion.button>
            ))}
          </div>

          {/* Right Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Switcher — Desktop hover dropdown */}
            <div className="relative group hidden md:block">
              <button className="flex items-center gap-1.5 text-gray-300 hover:text-white uppercase text-xs font-bold glass px-2.5 py-2 rounded-lg transition-all hover:bg-white/10">
                <span>{currentLang?.flag}</span>
                <span>{language.split('-')[0].toUpperCase()}</span>
                <SafeIcon icon={FiIcons.FiChevronDown} className="text-[10px] opacity-50 group-hover:rotate-180 transition-transform duration-300" />
              </button>
              <div className="absolute right-0 top-full mt-2 w-40 bg-[#1a1a24] border border-white/10 rounded-xl shadow-2xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangSelect(lang.code)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-white/5 transition-colors flex items-center gap-2 ${
                      language === lang.code ? 'text-orange-400 font-bold bg-white/5' : 'text-white'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    {lang.label}
                    {language === lang.code && (
                      <SafeIcon icon={FiIcons.FiCheck} className="ml-auto text-xs" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Language Switcher — Mobile click toggle */}
            <div className="relative md:hidden">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 text-gray-300 hover:text-white text-xs font-bold glass px-2.5 py-2 rounded-lg transition-all hover:bg-white/10"
              >
                <span>{currentLang?.flag}</span>
                <SafeIcon icon={FiIcons.FiChevronDown} className={`text-[10px] opacity-50 transition-transform duration-300 ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-full mt-2 w-40 bg-[#1a1a24] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLangSelect(lang.code)}
                        className={`w-full text-left px-4 py-3 text-sm hover:bg-white/5 transition-colors flex items-center gap-2 ${
                          language === lang.code ? 'text-orange-400 font-bold bg-white/5' : 'text-white'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        {lang.label}
                        {language === lang.code && (
                          <SafeIcon icon={FiIcons.FiCheck} className="ml-auto text-xs" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop CTA */}
            <motion.button
              onClick={handleCta}
              className="hidden md:flex items-center gap-2 bg-gradient-to-r from-orange-500 to-purple-600 text-white px-5 py-2 rounded-full font-bold text-sm shadow-lg hover:shadow-orange-500/30 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {text.cta}
            </motion.button>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden glass p-2 rounded-lg text-white"
            >
              <SafeIcon icon={mobileOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-xl" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Full-Screen Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 bg-[#0a0a0f] flex flex-col pt-24 px-6 md:hidden"
          >
            {/* Language selector inside mobile menu */}
            <div className="flex gap-2 mb-6">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLangSelect(lang.code)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                    language === lang.code
                      ? 'bg-orange-500/20 border-orange-500/40 text-orange-400'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>

            <div className="space-y-2">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="w-full text-left py-4 px-4 text-xl font-bold text-white hover:text-orange-400 transition-colors border-b border-white/5 flex items-center justify-between"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  {link.label}
                  <SafeIcon icon={FiIcons.FiArrowRight} className="text-gray-600" />
                </motion.button>
              ))}
            </div>

            <motion.button
              onClick={handleCta}
              className="mt-8 w-full py-4 bg-gradient-to-r from-orange-500 to-purple-600 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {text.cta}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;